import Mixpanel from '@/utils/analytics/mixpanel'
import { mergeSuperProperties } from '@/utils/analytics/userProperties'
import { mergeSessionUtmParams } from '@/utils/analytics/utmParams'
import { getClientName } from '@/utils/external'
import {
  buildMoengageEventParams,
  normalizeMoengagePageLocation,
  trackMoengageEvent,
} from '@/utils/moengage'

export type ProductEventParams = Record<
  string,
  string | number | boolean | undefined | null
>

const toGtagParams = (
  params: ProductEventParams,
): Record<string, string | number | boolean> => {
  const result: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    if (typeof value === 'object') {
      result[key] = JSON.stringify(value)
    } else {
      result[key] = value
    }
  }
  return result
}

/** Flatten GA `items` arrays into indexed scalar keys for MoEngage dashboards. */
export function flattenEcommerceParamsForMoengage(
  params: Record<string, unknown>,
): Record<string, unknown> {
  const { items, ...rest } = params
  if (!Array.isArray(items)) {
    return params
  }

  const flattened: Record<string, unknown> = {
    ...rest,
    item_count: items.length,
  }

  items.forEach((item, index) => {
    if (!item || typeof item !== 'object') return
    for (const [key, value] of Object.entries(item as Record<string, unknown>)) {
      if (value === undefined || value === null) continue
      flattened[`item_${index}_${key}`] = value
    }
  })

  return flattened
}

export function trackProductEvent(
  eventName: string,
  eventParams: ProductEventParams,
  extraParams: Record<string, unknown> = {},
) {
  if (typeof window === 'undefined') return

  const mergedEventParams = mergeSessionUtmParams(
    mergeSuperProperties(eventParams),
  )
  const gtagParams = toGtagParams(mergedEventParams)

  if (window.gtag != null) {
    window.gtag('event', eventName, gtagParams)
  }

  trackMoengageEvent(
    eventName,
    buildMoengageEventParams(mergedEventParams, extraParams),
  )

  Mixpanel.track(eventName, {
    ...gtagParams,
    ...extraParams,
  })
}

/** MoEngage only — GA page views are handled by GTM in _app to avoid double-counting. */
export function trackPageView(url: string, clientSrc: string) {
  if (typeof window === 'undefined') return

  trackMoengageEvent(
    'PageViewed',
    buildMoengageEventParams(
      mergeSessionUtmParams(
        mergeSuperProperties({
          page_location: normalizeMoengagePageLocation(url),
          client_src: clientSrc,
        }),
      ),
    ),
  )
}

export function mirrorEcommerceEventToMoengage(
  eventName: string,
  params: Record<string, unknown>,
) {
  if (typeof window === 'undefined') return

  trackMoengageEvent(
    eventName,
    buildMoengageEventParams(
      mergeSessionUtmParams(
        mergeSuperProperties({
          page_location: window.location?.href ?? '',
          client_src: getClientName(),
        }),
      ),
      flattenEcommerceParamsForMoengage(params),
    ),
  )
}
